﻿package org.zq.pixelset.processing

/**
 * 将多个文件打包为 ZIP（STORED，无压缩，适合已压缩的图片格式）。
 */
object ZipArchive {
    fun create(entries: List<Pair<String, ByteArray>>): ByteArray {
        require(entries.isNotEmpty()) { "ZIP 条目不能为空" }
        val files = dedupeEntryNames(entries)
        val output = ByteArrayOutput()
        val centralDirectory = ByteArrayOutput()
        var offset = 0

        for ((name, data) in files) {
            val nameBytes = name.encodeToByteArray()
            val crc = crc32(data)
            val localHeader = buildLocalFileHeader(nameBytes, data.size, crc)
            output.write(localHeader)
            output.write(data)
            centralDirectory.write(
                buildCentralDirectoryHeader(nameBytes, data.size, crc, offset),
            )
            offset += localHeader.size + data.size
        }

        val cdBytes = centralDirectory.toByteArray()
        output.write(cdBytes)
        output.write(buildEndOfCentralDirectory(cdBytes.size, offset, files.size))
        return output.toByteArray()
    }

    private fun dedupeEntryNames(entries: List<Pair<String, ByteArray>>): List<Pair<String, ByteArray>> {
        val used = mutableSetOf<String>()
        return entries.map { (name, bytes) ->
            var candidate = sanitizeEntryName(name)
            var index = 1
            while (!used.add(candidate)) {
                val dot = candidate.lastIndexOf('.')
                candidate = if (dot > 0) {
                    "${candidate.substring(0, dot)}_$index${candidate.substring(dot)}"
                } else {
                    "${candidate}_$index"
                }
                index++
            }
            candidate to bytes
        }
    }

    private fun sanitizeEntryName(name: String): String {
        val cleaned = name.replace('\\', '/').substringAfterLast('/')
        return cleaned.ifEmpty { "file" }
    }

    private fun buildLocalFileHeader(nameBytes: ByteArray, size: Int, crc: Int): ByteArray {
        val header = ByteArrayOutput()
        header.writeInt(LOCAL_FILE_HEADER_SIGNATURE)
        header.writeShort(20) // version needed
        header.writeShort(0) // flags
        header.writeShort(0) // compression: stored
        header.writeShort(0) // mod time
        header.writeShort(0) // mod date
        header.writeInt(crc)
        header.writeInt(size)
        header.writeInt(size)
        header.writeShort(nameBytes.size)
        header.writeShort(0) // extra length
        header.write(nameBytes)
        return header.toByteArray()
    }

    private fun buildCentralDirectoryHeader(
        nameBytes: ByteArray,
        size: Int,
        crc: Int,
        offset: Int,
    ): ByteArray {
        val header = ByteArrayOutput()
        header.writeInt(CENTRAL_DIRECTORY_SIGNATURE)
        header.writeShort(20) // version made by
        header.writeShort(20) // version needed
        header.writeShort(0)
        header.writeShort(0)
        header.writeShort(0)
        header.writeShort(0)
        header.writeInt(crc)
        header.writeInt(size)
        header.writeInt(size)
        header.writeShort(nameBytes.size)
        header.writeShort(0)
        header.writeShort(0)
        header.writeShort(0)
        header.writeShort(0)
        header.writeInt(0)
        header.writeInt(offset)
        header.write(nameBytes)
        return header.toByteArray()
    }

    private fun buildEndOfCentralDirectory(cdSize: Int, cdOffset: Int, entryCount: Int): ByteArray {
        val footer = ByteArrayOutput()
        footer.writeInt(END_OF_CENTRAL_DIRECTORY_SIGNATURE)
        footer.writeShort(0)
        footer.writeShort(0)
        footer.writeShort(entryCount)
        footer.writeShort(entryCount)
        footer.writeInt(cdSize)
        footer.writeInt(cdOffset)
        footer.writeShort(0)
        return footer.toByteArray()
    }

    private fun crc32(data: ByteArray): Int {
        var crc = 0xFFFFFFFF.toInt()
        for (b in data) {
            crc = crc xor (b.toInt() and 0xFF)
            for (k in 0 until 8) {
                crc = if (crc and 1 != 0) {
                    (crc ushr 1) xor 0xEDB88320.toInt()
                } else {
                    crc ushr 1
                }
            }
        }
        return crc.inv()
    }

    private class ByteArrayOutput {
        private val buffer = mutableListOf<Byte>()

        fun write(bytes: ByteArray) {
            for (b in bytes) buffer.add(b)
        }

        fun writeInt(value: Int) {
            writeShort(value and 0xFFFF)
            writeShort((value ushr 16) and 0xFFFF)
        }

        fun writeShort(value: Int) {
            buffer.add((value and 0xFF).toByte())
            buffer.add(((value ushr 8) and 0xFF).toByte())
        }

        fun toByteArray(): ByteArray = buffer.toByteArray()
    }

    private const val LOCAL_FILE_HEADER_SIGNATURE = 0x04034b50
    private const val CENTRAL_DIRECTORY_SIGNATURE = 0x02014b50
    private const val END_OF_CENTRAL_DIRECTORY_SIGNATURE = 0x06054b50
}
